<template>
  <div>
    <gl-map
      ref="mapContainer"
      :init-map-options="options"
      :init-map-position="position"
    />
    <div class="geocoder" @click="$emit('unselect')">
      <geocoder
        :search-bbox="[-79.76, 41.99, -57.1, 64.59]"
        :features="locationsGeojson.features"
        @results="addGeocoderMarker"
        @empty="removeGeocoderMarker"
      />
    </div>
  </div>
</template>

<script>
import GlMap from '@anagraph/gl-map'
import mapboxgl from 'mapbox-gl'
import { pointsWithinBounds } from '@/utils/turfHelper'
import { shallowRef, toRefs, watch } from 'vue'
import region from '../region'
import style from '@/voyager-style.json'
import Geocoder from '../commons/components/icherche-geocoder'
export default {
  components: {
    GlMap,
    Geocoder
  },
  props: {
    locationsGeojson: {
      type: Object,
      default: null
    },
    extentvalues: {
      type: Object,
      default: null
    },
    selected: {
      type: Object,
      default: null
    },
    filters: {
      type: Object,
      default: null
    }
  },
  mounted() {
    const vm = this
    const map = this.$refs.mapContainer.map
    map.on('load', () => {
      // adding the quebec geojson layer
      map.addSource('quebec-region', {
        type: 'geojson',
        data: region
      })
      map.addLayer({
        id: 'region-boundary',
        type: 'line',
        source: 'quebec-region',
        paint: {
          'line-color': '#FFA500',
          'line-width': 2
        }
      })
      // adding the locations data
      map.addSource('locations', {
        type: 'geojson',
        data: this.locationsGeojson,
        promoteId: 'id',
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 20
      })
      map.addLayer({
        id: 'locations-layer',
        type: 'symbol',
        source: 'locations',
        layout: {
          'icon-image': 'marker-15',
          'icon-allow-overlap': false,
          'icon-padding': 0
        }
      })
      map.on('idle', () => {
        const collection = pointsWithinBounds(
          {
            type: 'FeatureCollection',
            features: this.locationsGeojson.features
          },
          map.getBounds()
        )
        this.$emit('features', collection.features)
      })
      map.on('click', 'locations-layer', function (e) {
        e.preventDefault() // add this to make the click on the map possible too
        const locationinfo = e.features[0]
        vm.$emit('locationinfoselect', { locinfo: locationinfo })
      })
      // Change the cursor to a pointer when the mouse is over the places layer.
      map.on('mouseenter', 'locations-layer', function () {
        map.getCanvas().style.cursor = 'pointer'
      })
      // add this to make the click on the map possible and to close the infopanel
      map.on('click', function (e) {
        if (e.defaultPrevented === false) {
          vm.$emit('unselect')
        }
      })
      // Change it back to a pointer when it leaves.
      map.on('mouseleave', 'locations-layer', function () {
        map.getCanvas().style.cursor = ''
      })
      map.addLayer({
        id: 'locations-layer-selected',
        type: 'symbol',
        source: 'locations',
        layout: {
          'icon-image': 'marker-15-inverted',
          'icon-allow-overlap': false,
          'icon-padding': 0
        },
        filter: ['all', true, false]
      })

      map.on('mouseenter', 'locations-layer-selected', function () {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', 'locations-layer-selected', function () {
        map.getCanvas().style.cursor = ''
      })
      const clusterLayers = [
        {
          id: 'clusters-bg',
          type: 'circle',
          source: 'locations',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#fae604',
            'circle-stroke-width': 2,
            'circle-stroke-color': 'black',
            'circle-radius': 20
          }
        },
        {
          id: 'cluster-count',
          type: 'symbol',
          source: 'locations',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count'],
            'text-size': 12
          },
          paint: {
            'text-color': 'black'
          }
        }
      ]
      map.addLayer(clusterLayers[0])
      map.addLayer(clusterLayers[1])
      map.on('click', 'clusters-bg', e => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters-bg']
        })
        const clusterId = features[0].properties.cluster_id
        map
          .getSource('locations')
          .getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return
            if (zoom <= 17) {
              map.easeTo({
                center: features[0].geometry.coordinates,
                zoom: zoom
              })
            }
          })
      })
      // Add geolocate control to the map.
      // map.addControl(
      //   new mapboxgl.GeolocateControl({
      //     positionOptions: {
      //       enableHighAccuracy: true
      //     },
      //     trackUserLocation: true
      //   }),
      //   'top-left'
      // )
      this.$emit('loaded', true)
    })
  },
  methods: {
    addGeocoderMarker(center) {
      const map = this.$refs.mapContainer.map
      this._geocoderMarker = new mapboxgl.Marker().setLngLat(center).addTo(map)
      map.flyTo({ center, zoom: 11.5 })
    },
    removeGeocoderMarker() {
      if (this._geocoderMarker) this._geocoderMarker.remove()
    }
  },
  setup(props) {
    const mapContainer = shallowRef(null)
    const { extentvalues, filters, selected } = toRefs(props)
    const defaultZoom = 4
    const position = { center: { lng: -71.2036, lat: 53.9181 } }
    watch(extentvalues, types => {
      const map = mapContainer.value.map
      map.fitBounds([
        [types.xmin, types.ymin],
        [types.xmax, types.ymax]
      ])
    })

    watch(filters, newVal => {
      const categoryFilter = feature => {
        if (newVal.rocqtr && feature.properties.rocqtr !== 'Oui') return false
        if (newVal.attrueq && feature.properties.attrueq !== 'Oui') return false
        if (newVal.cami && feature.properties.cami === '') return false
        return true
      }

      const geojson = {
        ...props.locationsGeojson,
        features: props.locationsGeojson.features.filter(categoryFilter)
      }
      mapContainer.value.map.getSource('locations').setData(geojson)
    })

    watch(selected, newVal => {
      const id = newVal ? newVal.properties.id : false
      if (id) {
        mapContainer.value.map.setFilter('locations-layer-selected', [
          'in',
          'id',
          id
        ])
        mapContainer.value.map.flyTo({
          center: newVal.geometry.coordinates,
          zoom: 12.5
        })
      } else {
        mapContainer.value.map.setFilter('locations-layer-selected', [
          'all',
          true,
          false
        ])
      }
    })
    return {
      mapContainer,
      options: {
        style: {
          ...style,
          sprite:
            window.location.origin + process.env.BASE_URL + 'sprites/rocqtr'
        },
        zoom: defaultZoom
      },
      position
    }
  }
}
</script>

<style lang="scss" scoped>
.geocoder {
  position: absolute;
  z-index: 1000;
  top: 50px;
  right: 50px;
  @media (max-width: 1023px) {
    top: 12px;
  }
}
</style>
